<template>
  <template v-if="step === 'cash'">
    <DrawerHeader @close="emit('back')">
      Cash Tendered
    </DrawerHeader>
    <div class="flex flex-1 flex-col px-6">
      <div class="mb-4 flex flex-1 flex-col px-8">
        <div
          class="flex flex-1 flex-col items-center justify-center"
        >
          <CustomAmountDisplay :value="cashTendered" />
        </div>
        <div class="flex items-center justify-between">
          <h3 class="text-xs text-background-fg/50">
            Amount Due
          </h3>
          <p
            class="text-lg font-bold leading-tight text-background-fg/87"
          >
            {{ formatToMerchantCurrencyString(amountDue) }}
          </p>
        </div>
        <div
          v-if="!isGratuityEnabled"
          class="flex items-center justify-between"
        >
          <h3 class="text-xs text-background-fg/50">
            Change Due
          </h3>
          <p
            class="text-lg font-bold leading-tight text-background-fg/87"
          >
            {{ formatToMerchantCurrencyString(changeDue) }}
          </p>
        </div>
      </div>

      <BaseNumpad v-model="cashTendered" />
    </div>
    <div class="flex items-center justify-center p-4">
      <ActionButtonPrimary
        class="w-full"
        :disabled="cashSubmitDisabled"
        @click="onFinishCash"
      >
        Pay
        {{ formatToMerchantCurrencyString(cashTendered) }}
      </ActionButtonPrimary>
    </div>
  </template>
  <template v-else>
    <template v-if="isGratuityEnabled">
      <DrawerHeader @close="step = 'cash'">
        Change Given
      </DrawerHeader>
      <div class="flex flex-1 flex-col px-6">
        <div class="mb-4 flex flex-1 flex-col px-8">
          <div
            class="flex flex-1 flex-col items-center justify-center"
          >
            <CustomAmountDisplay :value="changeGiven" />
          </div>

          <div class="flex items-center justify-between">
            <!-- <StatRow label="CASH TENDERED" value="" -->
            <h3 class="text-xs text-background-fg/50">
              Change
            </h3>
            <p
              class="text-lg font-bold leading-tight text-background-fg/87"
            >
              {{
                formatToMerchantCurrencyString(changeGiven)
              }}
            </p>
          </div>
          <div class="flex items-center justify-between">
            <h3 class="text-xs text-background-fg/50">
              Amount Kept
            </h3>
            <p
              class="text-lg font-bold leading-tight text-background-fg/87"
            >
              {{
                formatToMerchantCurrencyString(
                  cashTendered - changeGiven
                )
              }}
            </p>
          </div>
          <div class="flex items-center justify-between">
            <h3 class="text-xs text-background-fg/50">
              Amount Due
            </h3>
            <p
              class="text-lg font-bold leading-tight text-background-fg/87"
            >
              {{
                formatToMerchantCurrencyString(amountDue)
              }}
            </p>
          </div>
        </div>
        <BaseNumpad v-model="changeGiven" />
      </div>

      <div
        class="flex flex-col items-center justify-center space-y-3 p-4"
      >
        <ActionButtonPrimary
          class="w-full"
          :disabled="changeSubmitDisabled"
          @click="onFinishChange"
        >
          <template v-if="changeGiven === 0">
            No change
          </template>
          <template v-else>
            Give
            {{
              formatToMerchantCurrencyString(changeGiven)
            }}
            Change
          </template>
        </ActionButtonPrimary>

        <ActionButtonSecondary
          class="w-full"
          @click="onGiveFullChange"
        >
          Give
          {{
            formatToMerchantCurrencyString(
              cashTendered - amountDue
            )
          }}
          Change
        </ActionButtonSecondary>
      </div>
    </template>
    <template v-else>
      <DrawerHeader @close="step = 'cash'">
        Change Given
      </DrawerHeader>
      <div class="flex flex-1 flex-col px-6">
        <div
          class="mb-4 flex flex-1 flex-col justify-end px-8"
        >
          <div class="flex items-center justify-between">
            <!-- <StatRow label="CASH TENDERED" value="" -->
            <h3 class="text-xs text-background-fg/50">
              Change
            </h3>
            <p
              class="text-lg font-bold leading-tight text-background-fg/87"
            >
              {{
                formatToMerchantCurrencyString(changeGiven)
              }}
            </p>
          </div>
          <div class="flex items-center justify-between">
            <h3 class="text-xs text-background-fg/50">
              Amount Kept
            </h3>
            <p
              class="text-lg font-bold leading-tight text-background-fg/87"
            >
              {{
                formatToMerchantCurrencyString(
                  cashTendered - changeGiven
                )
              }}
            </p>
          </div>
          <div class="flex items-center justify-between">
            <h3 class="text-xs text-background-fg/50">
              Amount Due
            </h3>
            <p
              class="text-lg font-bold leading-tight text-background-fg/87"
            >
              {{
                formatToMerchantCurrencyString(amountDue)
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-center p-4">
        <ActionButtonPrimary
          class="mt-auto w-full"
          :disabled="changeSubmitDisabled"
          @click="onFinishChange"
        >
          Give
          {{ formatToMerchantCurrencyString(changeGiven) }}
          Change
        </ActionButtonPrimary>
      </div>
    </template>
  </template>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  amountDue: number
}>()

const { isGratuityEnabled } = useTenant()
const emit = defineEmits<{
  (
    event: "submit",
    cashTendered: number,
    changeGiven: number,
    amountDue: number
  ): void
  (event: "back"): void
}>()

const step = ref<"cash" | "change">("cash")

const cashTendered = ref(0)
const changeGiven = ref(0)

const changeDue = computed(() =>
  Math.max(0, cashTendered.value - props.amountDue)
)

const cashSubmitDisabled = computed(
  () => cashTendered.value < props.amountDue
)

const changeSubmitDisabled = computed(
  () =>
    cashTendered.value - changeGiven.value < props.amountDue
)
const onFinishCash = () => {
  if (
    cashTendered.value > props.amountDue &&
    isGratuityEnabled.value
  ) {
    step.value = "change"
  } else if (cashTendered.value < props.amountDue) {
    return
  } else {
    changeGiven.value = cashTendered.value - props.amountDue

    if (changeGiven.value == 0) {
      // If no change to give, no need to show the change amount to them
      onFinishChange()
    } else {
      step.value = "change"
    }
  }
}

const onFinishChange = () => {
  emit(
    "submit",
    cashTendered.value,
    changeGiven.value,
    props.amountDue
  )
}

const onGiveFullChange = () => {
  emit(
    "submit",
    cashTendered.value,
    cashTendered.value - props.amountDue,
    props.amountDue
  )
}
</script>
